// dashboard/index.js

import React from "react";
import axios from "../../api";
import { useSelector } from "react-redux";
import GroupsIcon from "@mui/icons-material/Groups";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
} from "@mui/material";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import { BoxIcon } from "lucide-react";
import { SubscriptionsOutlined, Visibility } from "@mui/icons-material";
import { RootState } from "store";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import MUIDataTable, { MUIDataTableOptions } from "mui-datatables";
import { useNavigate } from "react-router-dom";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

interface CountItem {
  total_count?: number;
  status_id: number;
}
interface Divisions {
  division_name: string;
}
type SafePieLabelProps = {
  cx?: number | string;
  cy?: number | string;
  midAngle?: number;
  outerRadius?: number | string;
  percent?: number;
  name?: string;
};

const statusColors: Record<string, string> = {
  Open: "#f44336",
  ToDo: "#ff9800",
  "In-Progress": "#1976d2",
  "In Progress": "#1976d2",
  "On-Hold": "#0288d1",
  Pending: "#9c27b0",
  Done: "#4caf50",
  Closed: "#9e9e9e",
  ReOpen: "#ffa726",
  Cancelled: "#d32f2f",
  Escalated: "#d32f2f",
};
const BranchHeadDashboard = () => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const [divisionsPage, setDivisionsPage] = React.useState(0);
  const [divisionsRowsPerPage, setDivisionsRowsPerPage] = React.useState(5);

  const divisionsTableOptions: MUIDataTableOptions = React.useMemo(
    () => ({
      selectableRows: "none",
      selectableRowsHeader: false,
      elevation: 0,
      tableBodyHeight: "auto",
      responsive: "standard",
      page: divisionsPage,
      rowsPerPage: divisionsRowsPerPage,
      rowsPerPageOptions: [5, 10, 15, 25, 100],
      onChangePage: (newPage: number) => setDivisionsPage(newPage),
      onChangeRowsPerPage: (numRows: number) => {
        setDivisionsRowsPerPage(numRows);
        setDivisionsPage(0);
      },
    }),
    [divisionsPage, divisionsRowsPerPage]
  );
  const [assetsCount, setAssetsCount] = React.useState([]);
  const [subscriptionsCount, setSubscriptionsCount] = React.useState([]);
  const [ticketsCount, setTicketsCount] = React.useState([]);
  const [divisionsCount, setDivisionsCount] = React.useState([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [rows, setRows] = React.useState<Divisions[]>([]);
  const [counts, setCounts] = React.useState<Record<string, CountItem>>({});
  const cardStyle = {
    height: "100%",
    boxShadow: 3,
    borderRadius: 2,
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      boxShadow: 5,
      transform: "scale(1.05)",
    },
  };
  const quickActions = [
    {
      label: "View Assets",
      icon: <Visibility sx={{ color: "var(--primary-color, #2e7d32)", fontSize: 32 }} />,
      onClick: () => navigate("/branch-head/assets-list"),
    },
    {
      label: "View Subscription",
      icon: <Visibility sx={{ color: "var(--primary-color, #2e7d32)", fontSize: 32 }} />,
      onClick: () => navigate("/branch-head/subscriptions-list"),
    },
    {
      label: "View Tickets",
      icon: <Visibility sx={{ color: "var(--primary-color, #2e7d32)", fontSize: 32 }} />,
      onClick: () => navigate("/branch-head/tickets-list"),
    },
  ];

  const fetchAssets = React.useCallback(async () => {
    try {
      const response = await axios.get(`/assets/branch-head/${user?.userId}`);
      const assets = response?.data?.list ?? [];
      setAssetsCount(assets?.length);
    } catch (error) {
      console.error(error);
    }
  }, []);
  React.useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);
  const fetchSubscriptions = React.useCallback(async () => {
    try {
      const response = await axios.get(
        `/subscription/branch-head/${user?.userId}`
      );
      const subscriptions = response?.data ?? [];
      setSubscriptionsCount(subscriptions?.length);
    } catch (error) {
      console.error(error);
    }
  }, []);
  React.useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);
  const fetchTickets = React.useCallback(async () => {
    try {
      const response = await axios.get(`/tickets/branch-head/${user?.userId}`);
      const tickets = response?.data?.list ?? [];
      setTicketsCount(tickets?.length);
    } catch (error) {
      console.error(error);
    }
  }, []);
  React.useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);
  const fetchDivisions = React.useCallback(async () => {
    try {
      const response = await axios.get(
        `/employee/branch-head/divisions/${user?.userId}`
      );
      const divisions = response?.data?.divisions ?? [];
      setDivisionsCount(divisions?.length);
    } catch (error) {
      console.error(error);
    }
  }, []);
  React.useEffect(() => {
    fetchDivisions();
  }, [fetchDivisions]);
  const fetchDivisionsByBrnachHead = React.useCallback(async () => {
    try {
      const response = await axios.get(
        `/divisions/branch-head/${user?.userId}`
      );
      const divisions = response?.data?.divisions ?? [];
      setRows(divisions);
    } catch (error) {
      console.error(error);
    }
  }, []);
  React.useEffect(() => {
    fetchDivisionsByBrnachHead();
  }, [fetchDivisionsByBrnachHead]);
  const fetchTicketCounts = React.useCallback(async () => {
    try {
      const response = await axios.get(
        `/tickets/branch-head/ticket-counts/${user?.userId}`
      );
      const filteredCounts = response.data.list as Record<string, CountItem>;

      const newTotalCount = Object.values(filteredCounts).reduce(
        (acc, curr) => acc + (curr.total_count ?? 0),
        0
      );

      setCounts(filteredCounts);
      setTotalCount(newTotalCount);
    } catch (error) {
      console.error(error);
    }
  }, [user]);
  React.useEffect(() => {
    fetchTicketCounts();
  }, [fetchTicketCounts]);
  const statusColorValues = Object.values(statusColors);
  const pieData = Object.entries(counts).map(
    ([status, { total_count }], index) => ({
      name: status,
      value: total_count ?? 0,
      percentage: (((total_count ?? 0) / totalCount) * 100).toFixed(2),
      color: statusColorValues[index % statusColorValues.length],
    })
  );
  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle = 0,
    outerRadius,
    percent = 0,
  }: SafePieLabelProps) => {
    const cxNum = typeof cx === "number" ? cx : 0;
    const cyNum = typeof cy === "number" ? cy : 0;
    const outerNum = typeof outerRadius === "number" ? outerRadius : 0;
    const radius = outerNum + 30; // now SAFE
    const x = cxNum + radius * Math.cos(-midAngle * RADIAN);
    const y = cyNum + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#333"
        textAnchor={x > cxNum ? "start" : "end"}
        dominantBaseline="central"
        fontSize={12}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };
  const columns = [
    { name: "division_name", label: "Division" },
    { name: "customer_name", label: "Company" },
  ];
  const handleClick = (status_id: string | number) => {
    navigate(`/branch-head/tickets-list?status_id=${status_id}`);
  };
  return (
    <Box p={3}>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card
            sx={{
              ...cardStyle,
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                boxShadow: 6,
                transform: "scale(1.05)",
              },
            }}
          >
            <CardContent
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Total Divisions
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                  <strong>{divisionsCount}</strong>
                </Typography>
              </Box>

              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: "10%",
                  background: "#e1f2f2",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <BoxIcon color="black" size={28} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card
            sx={{
              ...cardStyle,
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                boxShadow: 6,
                transform: "scale(1.05)",
              },
            }}
          >
            <CardContent
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Total Assets
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                  <strong>{assetsCount}</strong>
                </Typography>
              </Box>

              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: "10%",
                  background: "#e9e0fd",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <BoxIcon color="black" size={28} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card
            sx={{
              ...cardStyle,
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                boxShadow: 6,
                transform: "scale(1.05)",
              },
            }}
          >
            <CardContent
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Subscriptions
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                  <strong>{subscriptionsCount}</strong>
                </Typography>
              </Box>

              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: "10%",
                  background: "#fdf5e1",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <SubscriptionsOutlined sx={{ color: "white", fontSize: 28 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card
            sx={{
              ...cardStyle,
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                boxShadow: 6,
                transform: "scale(1.05)",
              },
            }}
            // onClick={() => navigate("/customers-list")}
          >
            <CardContent
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Total Tickets
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                  <strong>{ticketsCount}</strong>
                </Typography>
              </Box>

              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: "10%",
                  background: "#e1f2f2",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <GroupsIcon sx={{ color: "white", fontSize: 28 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Grid container spacing={3} mb={5}>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              height: "100%",
              boxShadow: 3,
              borderRadius: 2,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                boxShadow: 3,
                transform: "scale(1.05)",
              },
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", mb: 2, textAlign: "left" }}
              >
                Ticket Status Distribution
              </Typography>

              {totalCount > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: 400,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          dataKey="value"
                          labelLine
                          label={(props) =>
                            renderCustomizedLabel(props as SafePieLabelProps)
                          }
                        >
                          {pieData.map((entry) => (
                            <Cell
                              key={entry.name}
                              fill={statusColors[entry.name] ?? "#757575"}
                            />
                          ))}
                        </Pie>

                        <Tooltip
                          formatter={(value, name, { payload }) => [
                            `${value} (${payload.percentage}%)`,
                            name,
                          ]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>

                  {/* Custom legend */}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "center",
                      gap: 1,
                      mt: 2,
                      maxHeight: 100,
                      overflowY: "auto",
                    }}
                  >
                    {pieData.map((entry) => (
                      <Box
                        key={entry.name}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mr: 2,
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            backgroundColor:
                              statusColors[entry.name] ?? "#757575",
                            mr: 1,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "1rem", fontWeight: "bold" }}
                        >
                          {entry.name}: {entry.percentage}%
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" align="center" sx={{ mt: 5 }}>
                  No ticket data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              height: "100%",
              boxShadow: 3,
              borderRadius: 2,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                boxShadow: 3,
                transform: "scale(1.05)",
              },
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Status Overview
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  mt: 2,
                }}
              >
                {/* Total Tickets */}
                <Chip
                  label={`Total tickets ${totalCount}`}
                  sx={{
                    minWidth: 120,
                    bgcolor: "teal",
                    fontWeight: 700, // makes text bold
                    color: "#fff", // text color
                    transition: "all 0.2s ease-in-out",
                    "& .MuiChip-label": {
                      color: "#fff",
                      fontWeight: 700,
                    }, // enforce white
                    "&:hover": {
                      boxShadow: 3,
                      transform: "scale(1.05)",
                      bgcolor: "#000000",
                    },
                  }}
                  onClick={() => navigate("/branch-head/tickets-list")}
                />
                {/* Individual Status Chips */}
                {Object.entries(counts)
                  .filter(([status]) => status !== "Open")
                  .map(([status, { status_id, total_count }]) => {
                    return (
                      <Chip
                        key={status_id}
                        label={`${status} ${total_count}`}
                        sx={{
                          minWidth: 120,
                          color: "#fff",
                          fontWeight: 700,
                          bgcolor: statusColors[status] ?? "#757575",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            boxShadow: 3,
                            transform: "scale(1.05)",
                            bgcolor: "#000000",
                          },
                        }}
                        onClick={() => handleClick(status_id)}
                      />
                    );
                  })}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              height: "100%",
              boxShadow: 3,
              borderRadius: 2,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                boxShadow: 3,
                transform: "scale(1.05)",
              },
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                Quick Actions
              </Typography>

              <Grid container spacing={2}>
                {quickActions.map((action) => (
                  <Grid item xs={12} sm={6} key={action.label}>
                    <Box
                      sx={{
                        height: 110, // equal height
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 2,
                        borderRadius: 2,
                        bgcolor: "#f9fafb",
                        width: "100%",
                        minHeight: 100, // keeps height consistent
                        boxShadow: 2,
                        cursor: "pointer",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          boxShadow: 4,
                          transform: "none",
                        },
                      }}
                      onClick={action.onClick}
                    >
                      <IconButton
                        sx={{
                          background: "white",
                          mb: 1,
                        }}
                      >
                        {action.icon}
                      </IconButton>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          fontSize: 12,
                          lineHeight: 1.1,
                          textAlign: "center",
                          whiteSpace: "normal", // allow wrapping
                          wordBreak: "break-word", // break long words
                          maxWidth: "100%", // don't overflow outside box
                        }}
                      >
                        {action.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Grid item xs={12} marginBottom={1}>
        <ThemeProvider theme={getMuiTheme}>
          <MUIDataTable
            title={"Divisions"}
            data={rows}
            columns={columns}
            options={divisionsTableOptions}
          />
        </ThemeProvider>
      </Grid>
    </Box>
  );
};

export default BranchHeadDashboard;
